// seo.service.ts
import { Inject, Injectable } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

export interface SeoOptions {
  title?: string;
  description?: string;
  og?: boolean;
  suffix?: string;
  preffix?: string;
  canonical?: string;
  image?: string;
}

export interface SeoSnapshot {
  title: string;
  description: string;
  canonical: string;
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly MAX_TITLE = 70; // Cambiado de 70 a 60 para cumplir con 50-60 caracteres
  private readonly MAX_DESC = 140;
  private readonly DEFAULT_TITLE = 'Archivo Histórico';
  private readonly DEFAULT_DESC  = 'Fotografías y publicaciones del Archivo Histórico del diario Oficial El Peruano';
  private readonly DEFAULT_SUFFIX = 'Diario Oficial El Peruano';
  private readonly DEFAULT_PREFFIX = '';
  private readonly DEFAULT_IMAGE = 'assets/image/elperuano_cuadrado_201.png';

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private doc: Document
  ) {}

  /** Canonical base por defecto, derivado del host + baseHref (dinámico) */
  private get defaultCanonical(): string {
    const w = this.doc?.defaultView;
    const origin = w?.location?.origin ?? 'http://localhost';
    const baseHref = this.getPublicBaseHref(origin); // ej: "/" o "/app/"
    return this.ensureTrailingSlash(`${origin}${baseHref}`);
  }

  setMeta(opts: SeoOptions = {}) {
    const suffix = opts.suffix ?? this.DEFAULT_SUFFIX;
    const preffix = opts.preffix ?? this.DEFAULT_PREFFIX;

    const finalTitle = this.composeTitle(preffix, opts.title || this.DEFAULT_TITLE, suffix);

    const finalDesc  = opts.description || this.DEFAULT_DESC;

    this.title.setTitle(finalTitle);
    this.meta.updateTag({ name: 'description', content: finalDesc });

    if (opts.og) {
      const imageUrl = this.resolveImageUrl(opts.image || this.DEFAULT_IMAGE);

      // Open Graph (Facebook, WhatsApp)
      this.meta.updateTag({ property: 'og:title', content: finalTitle });
      this.meta.updateTag({ property: 'og:description', content: finalDesc });
      this.meta.updateTag({ property: 'og:image', content: imageUrl });
      this.meta.updateTag({ property: 'og:type', content: 'website' });
      this.meta.updateTag({ property: 'og:site_name', content: 'Archivo Histórico' });

      // Twitter Card (X.com)
      this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
      this.meta.updateTag({ name: 'twitter:title', content: finalTitle });
      this.meta.updateTag({ name: 'twitter:description', content: finalDesc });
      this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
    }

    this.setCanonical(opts.canonical ?? this.defaultCanonical);
  }

  /** Agrega o actualiza la etiqueta canonical */
  setCanonical(url: string) {
    let link: HTMLLinkElement =
      this.doc.querySelector(`link[rel="canonical"]`) || this.doc.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    if (!link.parentNode) {
      this.doc.head.appendChild(link);
    }
  }

  /** Convierte la URL actual del browser en una canonical ABSOLUTA.
   * - Si usas hash (#/ruta), la reescribe como /ruta (sin #) porque Google ignora el hash.
   * - Mantiene ?query si existe. Elimina el fragmento #.
   */
  setCanonicalFromCurrentUrl() {
    const loc = this.doc.defaultView!.location;
    let href = loc.origin;

    if (loc.hash && loc.hash.startsWith('#/')) {
      const [hashPath, hashQuery] = loc.hash.substring(1).split('?'); // "/dt/d/241" | "x=1"
      href += hashPath || '';
      if (hashQuery) href += `?${hashQuery}`;
    } else {
      href += loc.pathname + loc.search;
    }

    this.setCanonical(href);
    return href;
  }

  /** Construye una canonical absoluta a partir de un path (sin hash). */
  setCanonicalFromPath(path: string) {
    const loc = this.doc.defaultView!.location;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const href = `${loc.origin}${cleanPath}`;
    this.setCanonical(href);
    return href;
  }

  /** Toma los valores actuales para poder restaurarlos luego */
  snapshot(): SeoSnapshot {
    const currentTitle = this.title.getTitle() || this.DEFAULT_TITLE;
    const descTag = this.meta.getTag('name="description"');
    const currentDesc = descTag?.content || this.DEFAULT_DESC;
    const linkTag = this.doc.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const currentCanonical = linkTag?.href || this.defaultCanonical;
    return { title: currentTitle, description: currentDesc, canonical: currentCanonical };
  }

  /** Restaura un snapshot previo */
  apply(snapshot: SeoSnapshot, og = true) {
    this.setMeta({
      title: snapshot.title,
      description: snapshot.description,
      canonical: snapshot.canonical,
      og
    });
  }

  /** Fuerza los defaults globales (dinámicos por host) */
  resetToDefaults(og = true) {
    this.setMeta({
      title: this.DEFAULT_TITLE,
      description: this.DEFAULT_DESC,
      canonical: this.defaultCanonical,
      og
    });
  }

  // ----------------- helpers -----------------

  /** Convierte una ruta relativa de imagen en absoluta */
  private resolveImageUrl(imagePath: string): string {
    // Si ya es una URL absoluta, retornarla tal cual
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    // Convertir ruta relativa a absoluta
    const loc = this.doc.defaultView!.location;
    const baseHref = this.getPublicBaseHref(loc.origin);
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    return `${loc.origin}${baseHref}${cleanPath}`;
  }

  private composeTitle(preffix: string, base: string, suffix: string) {
    const parts: string[] = [];
    if (preffix) parts.push(preffix.trim());
    if (base) parts.push(base.trim());
    if (suffix) parts.push(suffix.trim());
    return parts.join(' | ');
  }

  private truncate(text: string, max: number) {
    const clean = (text || '').replace(/\s+/g, ' ').trim();
    if (clean.length <= max) return clean;
    return clean.slice(0, max - 1).trimEnd() + '…';
  }

  /** Lee el <base href>. Si viene relativo, lo resuelve contra origin.
   * Devuelve siempre una ruta que empieza y termina con “/”.
   */
  private getPublicBaseHref(origin: string): string {
    const baseEl = this.doc.querySelector('base');
    let baseAttr = baseEl?.getAttribute('href') ?? '/';
    try {
      // new URL resuelve relativo a origin si corresponde
      const u = new URL(baseAttr, origin);
      return this.ensureLeadingAndTrailingSlash(u.pathname);
    } catch {
      return this.ensureLeadingAndTrailingSlash(baseAttr);
    }
  }

  private ensureTrailingSlash(s: string) {
    return s.endsWith('/') ? s : s + '/';
  }

  private ensureLeadingAndTrailingSlash(s: string) {
    let r = s.startsWith('/') ? s : '/' + s;
    return this.ensureTrailingSlash(r);
  }
}
